import type { Enrollment, EnrollmentComponent } from '../../shared/types/common.model';
import { LupHelper } from './lup-helper';
import { isNumber } from './utils';

/**
 * Helper class for enrollment related operations
 *
 * NOTE: This code was ported from AngularJS {@link https://scm.lup.ie/engineering/LearnUpon/-/blob/main/client/main_app/javascripts/factories/enrollment-helper.js}
 */
export class EnrollmentHelper {
  private helper: LupHelper;

  constructor(helper: LupHelper = new LupHelper()) {
    this.helper = helper;
  }

  /**
   * Determines if the learner can access the current module if the course is sequenced.
   * it does so by looking at the component to load but also the previous component in the list
   * as we want to see the status of the previous component should this one be completed or not.
   *
   * @param  enrollment -  Enrollment related whose components we are checking
   * @param  enrollment_component - Current enrollment component
   * @param  enrollment_components - Array of the enrollment components
   * @returns learner can access the current module or not
   */
  sequenceBlocked(
    enrollment: Enrollment,
    enrollment_component: EnrollmentComponent,
    enrollment_components: EnrollmentComponent[]
  ): boolean {
    const helper = this.helper;
    // if sequencing is not enabled, then just return false
    if (
      enrollment.enable_sequencing != 1 ||
      [helper.enrollmentStatuses.completed, helper.enrollmentStatuses.passed].includes(enrollment.status_id)
    ) {
      return false;
    }

    // const component_status = enrollment_component.enrollment_component_status_id;
    const component_type = enrollment_component.component_type_id;

    // if the component is not a session, then check, otherwise ILT sessions are always accessible
    if (component_type === helper.componentTypes.session || component_type === helper.componentTypes.training) {
      return false;
    }

    // If the survey is mandatory, and the learner has to complete that survey but the course is already failed
    // then we should allow them to access the survey because it might be the last module in the course and its mandatory for completion...
    if (
      enrollment_component.is_survey == 1 &&
      enrollment_component.survey_is_mandatory == 1 &&
      enrollment.status_id == helper.enrollmentStatuses.failed
    ) {
      return false;
    }

    // We need to check have any of the previous components need to be completed befor this one
    return this.dontHaveUncompletedModule(enrollment_components, enrollment_component, enrollment);
  }

  /**
   * Check have some enrollment component before current one ned to be completed before you can access current one
   * @param enrollmentComponents - Array of the enrollment components
   * @param enrollmentComponent -  Current enrollment component
   * @param enrollment - Enrollment related whose components we are checking
   * @returns true if no enrollment components need to be completed before current one
   */
  private dontHaveUncompletedModule(
    enrollmentComponents: EnrollmentComponent[],
    enrollmentComponent: EnrollmentComponent,
    enrollment: Enrollment
  ) {
    const helper = this.helper;
    let allGood = true;

    const componentIndex = enrollmentComponents.findIndex((item) => item.id === enrollmentComponent.id);

    enrollmentComponents.forEach((enrollmentComponent) => {
      const ecIndex = enrollmentComponents.findIndex((item) => item.id === enrollmentComponent.id);
      if (isNumber(componentIndex) && isNumber(ecIndex) && ecIndex < componentIndex) {
        // if the previous component has override sequencing going on, then allow access to this module
        // if they completed or passed the previous module, then allow access on this module
        // if they are pending review on the previous module and by pass sequencing on assignments is enabled, then allow access
        // if current module is in pending review and by pass sequencing for checklists as turned on
        // if they completed the previous and the previous module was a knowledge check or survey (or the overall pass mark was null on the course)
        // if it's rejected assignment
        if (
          !enrollmentComponent.override_sequencing &&
          !(
            enrollmentComponent.enrollment_component_status_id == helper.enrollmentStatuses.completed ||
            enrollmentComponent.enrollment_component_status_id == helper.enrollmentStatuses.passed
          ) &&
          !(
            (enrollmentComponent.enrollment_component_status_id == helper.enrollmentStatuses.pending_review ||
              (enrollmentComponent.enrollment_component_status_id == helper.enrollmentStatuses.in_progress &&
                enrollmentComponent.was_rejected)) &&
            enrollment.bypass_sequencing_on_assignments == 1
          ) &&
          !(
            enrollmentComponent.enrollment_component_status_id == helper.enrollmentStatuses.pending_review &&
            enrollment.bypass_sequencing_on_checklists == 1 &&
            enrollmentComponent.component_type_id === helper.componentTypes.checklist
          ) &&
          !(
            enrollmentComponent.enrollment_component_status_id >= helper.enrollmentStatuses.completed &&
            (isNumber(enrollment.pass_mark) ||
              (enrollmentComponent.enrollment_exam_status_id &&
                (enrollmentComponent.is_knowledge_test == 1 || enrollmentComponent.is_survey == 1)))
          ) &&
          !(
            enrollmentComponent.enrollment_component_status_id == helper.enrollmentStatuses.in_progress &&
            enrollmentComponent.was_rejected
          ) &&
          !(
            enrollmentComponent.component_type_id === helper.componentTypes.e_signature &&
            enrollment.bypass_sequencing_on_esignatures === 1
          )
        ) {
          allGood = false;
        }
        // Block next module if previous one type=checklist and EC status is pending_review and bypass_sequencing_on_checklists is turned off
        if (
          !enrollmentComponent.override_sequencing &&
          enrollmentComponent.component_type_id === helper.componentTypes.checklist &&
          enrollmentComponent.enrollment_component_status_id === helper.enrollmentStatuses.pending_review &&
          enrollment.bypass_sequencing_on_checklists === 0
        ) {
          allGood = false;
        }
      }
    });

    return allGood;
  }

  moduleReleased(enrollment_component: EnrollmentComponent, enable_module_release_dates: boolean): boolean {
    const utc_released_at = new Date(enrollment_component.release_at).getTime();
    return !(enable_module_release_dates && utc_released_at > Date.now());
  }
}
